// ============================================================================
// CHANGO V5.0 — D3 Quotes List
// ============================================================================
// Listing + filtering of a workspace's quotes. Columns vary by role:
//   - boss    : customer, amount, status, margin, owner
//   - finance : customer, amount, status, margin, owner
//   - sales   : customer, amount, status, owner  (NO margin/cost column)
//   - admin   : (not expected here; ops console instead)
//
// §20 defense-in-depth:
//   1. Backend has already stripped sensitive keys before the row arrived.
//   2. <HideFromSales> wraps the margin column so even if the API leaks, it
//      does not render for sales users.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { HideFromSales } from '../../components/RoleGate.jsx';
import ExcelImportModal from '../../components/ExcelImportModal.jsx';

const STATUS_LABELS = {
  draft:        { label: '草稿',    tone: 'bg-slate-100 text-slate-700' },
  pending:      { label: '待审',    tone: 'bg-amber-100 text-amber-800' },
  approved:     { label: '已审',    tone: 'bg-emerald-100 text-emerald-800' },
  rejected:     { label: '已拒',    tone: 'bg-rose-100 text-rose-800' },
  issued:       { label: '已出具',  tone: 'bg-sky-100 text-sky-800' },
  closed_won:   { label: '成交',    tone: 'bg-green-100 text-green-800' },
  closed_lost:  { label: '流失',    tone: 'bg-slate-200 text-slate-600' },
  archived:     { label: '归档',    tone: 'bg-slate-100 text-slate-500' }
};

function StatusPill({ status }) {
  const meta = STATUS_LABELS[status] || { label: status || '—', tone: 'bg-slate-100 text-slate-700' };
  return (
    <span className={['rounded px-2 py-0.5 text-xs font-medium', meta.tone].join(' ')}>
      {meta.label}
    </span>
  );
}

function formatCurrency(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('zh-CN', {
    style: 'currency', currency: 'CNY',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

export default function QuotesList() {
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get('status') || '';
  const qFilter      = params.get('q')      || '';

  const [rows, setRows]   = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (statusFilter) query.set('status', statusFilter);
    if (qFilter)      query.set('q', qFilter);
    query.set('limit', '50');

    (async () => {
      try {
        const res = await api.get(`/api/quotes?${query.toString()}`);
        if (cancelled) return;
        setRows(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number.isInteger(res?.total) ? res.total : 0);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setRows([]); setTotal(0);     // backend D3 endpoint pending
        } else {
          setError(err.message || '加载报价列表失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter, qFilter]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else       next.delete(key);
    setParams(next, { replace: true });
  };

  const hasMargin = useMemo(() =>
    rows.some((r) => r.margin_rmb != null || r.profit_rmb != null), [rows]);

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">报价</h1>
          <p className="text-sm text-slate-500">
            共 {total} 条 · 仅显示当前工作区
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setFilter('status', e.target.value)}
            className="input-text"
          >
            <option value="">全部状态</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <input
            type="search"
            placeholder="搜索客户/编号…"
            defaultValue={qFilter}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setFilter('q', e.currentTarget.value.trim());
            }}
            className="input-text w-56"
          />
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary"
          >
            📥 批量导入
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              const qs = new URLSearchParams();
              if (statusFilter) qs.set('status', statusFilter);
              if (qFilter) qs.set('q', qFilter);
              window.open(`/api/quotes/export-excel?${qs.toString()}`, '_blank');
            }}
          >
            导出 Excel
          </button>
          <Link to="/app/quotes/new" className="btn-primary">
            新建报价
          </Link>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">编号</th>
              <th className="px-4 py-3">客户</th>
              <th className="px-4 py-3">金额</th>
              <th className="px-4 py-3">状态</th>
              <HideFromSales>
                {hasMargin && <th className="px-4 py-3">毛利</th>}
              </HideFromSales>
              <th className="px-4 py-3">负责人</th>
              <th className="px-4 py-3">更新时间</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">加载中…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  暂无报价 · <Link to="/app/quotes/new" className="text-chango-700 hover:underline">创建第一条</Link>
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs">
                  <Link to={`/app/quotes/${r.id}`} className="text-chango-700 hover:underline">
                    {r.code || `#${r.id}`}
                  </Link>
                </td>
                <td className="px-4 py-3">{r.customer_name || '—'}</td>
                <td className="px-4 py-3 tabular-nums">{formatCurrency(r.amount_rmb)}</td>
                <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                <HideFromSales>
                  {hasMargin && (
                    <td className="px-4 py-3 tabular-nums">
                      {formatCurrency(r.margin_rmb ?? r.profit_rmb)}
                    </td>
                  )}
                </HideFromSales>
                <td className="px-4 py-3 text-slate-600">{r.owner_display_name || r.owner_name || r.owner_email || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{r.updated_at || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        提示：列表字段由后端 <code>stripSensitiveForSales</code> 中间件过滤；本页再作前端屏蔽（双重防护）。
      </p>

      {showImportModal && (
        <ExcelImportModal
          entity="quotes"
          onClose={() => setShowImportModal(false)}
          onComplete={() => {
            setShowImportModal(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
